import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { type RichText } from '@atlaskit/linking-types';
import { logError } from '@databases/logs';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type JiraIssueColumnData,
  type JiraIssueRef,
  type ResolvedJiraIssueRef,
} from '@databases/types/confluence';
import {
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type FieldWithContext,
} from '@databases/types/database';
import {
  type DatabaseFieldFilterType,
  DatabaseFilterType,
} from '@databases/types/database/view';
import { parseISO } from 'date-fns';
import { isEqual } from 'lodash-es';

import { parseStorageFormat } from '../../lib/dom';
import { loadJiraIssue } from '../../redux/jira/actions';
import { selectJiraIssueOptional } from '../../redux/jira/selectors';
import { encodeHtml } from '../../utils';
import type { FieldType } from '../field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase,
} from '../utils';

import { isNonEmptyJiraIssueValue } from './jiraIssue';

type JiraFieldEquivalentType =
  | DatabaseFieldType.DATE
  | DatabaseFieldType.USER
  | DatabaseFieldType.NUMBER
  | DatabaseFieldType.SELECT
  | DatabaseFieldType.TEXT;

export function getJiraFieldEquivalentType(
  jiraColumnType: string | undefined
): JiraFieldEquivalentType {
  switch (jiraColumnType) {
    case 'datetime':
    case 'date':
      return DatabaseFieldType.DATE;
    case 'user':
      return DatabaseFieldType.USER;
    case 'number':
      return DatabaseFieldType.NUMBER;
    case 'status':
    case 'tag':
    case 'icon':
      return DatabaseFieldType.SELECT;
    default:
      return DatabaseFieldType.TEXT;
  }
}

const FILTER_TYPES_BY_EQUIVALENT_TYPE: Record<
  JiraFieldEquivalentType,
  DatabaseFieldFilterType[]
> = {
  [DatabaseFieldType.DATE]: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.LESS,
  ],
  [DatabaseFieldType.USER]: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],
  [DatabaseFieldType.NUMBER]: [
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.LESS,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.EQUALS,
  ],
  [DatabaseFieldType.SELECT]: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],
  [DatabaseFieldType.TEXT]: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],
};

export function getJiraFieldFilterTypes(
  jiraColumnType: string | undefined
): DatabaseFieldFilterType[] {
  const equivalentType = getJiraFieldEquivalentType(jiraColumnType);
  return FILTER_TYPES_BY_EQUIVALENT_TYPE[equivalentType];
}

export type JiraIssuesState = Record<string, Record<string, ResolvedJiraIssueRef>>;

function getJiraColumnsFromState(
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
  jiraIssues: JiraIssuesState | undefined
): JiraIssueColumnData[] {
  const jiraField = field.settings.jiraField;
  if (!jiraField || !jiraIssues) {
    return [];
  }

  const columns: JiraIssueColumnData[] = [];
  for (const issues of Object.values(jiraIssues)) {
    for (const issue of Object.values(issues)) {
      const column = issue.columns[jiraField];
      if (column) {
        columns.push(column);
      }
    }
  }

  return columns;
}

export function getJiraColumnTypeFromState(
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
  jiraIssues: JiraIssuesState | undefined
): string | undefined {
  const columns = getJiraColumnsFromState(field, jiraIssues);
  return columns.find((column) => column.type)?.type;
}

export function selectEquivalentFieldType(
  field: DatabaseField | undefined,
  jiraIssues: JiraIssuesState | undefined
): DatabaseFieldType | undefined {
  if (!field) {
    return undefined;
  }

  if (field.type !== DatabaseFieldType.JIRA_ISSUE_DETAILS) {
    return field.type;
  }

  const jiraColumnType = getJiraColumnTypeFromState(
    field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
    jiraIssues
  );
  return getJiraFieldEquivalentType(jiraColumnType);
}

interface TagData {
  text: string;
}

export interface JiraColumnOption {
  text: string;
  data?: JiraIssueColumnData['data'] | TagData;
}

function isTagDataArray(data: unknown): data is TagData[] {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof (data[0] as TagData)?.text === 'string'
  );
}

export function getUniqueJiraColumnValues(
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
  jiraIssues: JiraIssuesState | undefined
): JiraColumnOption[] {
  const columns = getJiraColumnsFromState(field, jiraIssues);
  const uniqueValues = new Map<string, JiraColumnOption>();

  for (const column of columns) {
    if (column.type === 'tag' && isTagDataArray(column.data)) {
      for (const tag of column.data) {
        if (tag.text && tag.text.trim() !== '' && !uniqueValues.has(tag.text)) {
          uniqueValues.set(tag.text, {
            text: tag.text,
            data: tag,
          });
        }
      }
    } else if (column.text && column.text.trim() !== '') {
      if (!uniqueValues.has(column.text)) {
        uniqueValues.set(column.text, {
          text: column.text,
          data: column.data,
        });
      }
    }
  }

  return Array.from(uniqueValues.values()).sort((a, b) => a.text.localeCompare(b.text));
}

export interface JiraFieldInfo {
  jiraColumnType: string | undefined;
  jiraFieldName: string | undefined;
  equivalentType: JiraFieldEquivalentType;
  isTagType: boolean;
}

export function selectJiraFieldInfo(
  field: DatabaseField | undefined,
  jiraIssues: JiraIssuesState | undefined
): JiraFieldInfo | undefined {
  if (!field || field.type !== DatabaseFieldType.JIRA_ISSUE_DETAILS) {
    return undefined;
  }

  const jiraField = field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>;
  const jiraColumnType = getJiraColumnTypeFromState(jiraField, jiraIssues);
  const equivalentType = getJiraFieldEquivalentType(jiraColumnType);

  return {
    jiraColumnType,
    jiraFieldName: jiraField.settings.jiraField,
    equivalentType,
    isTagType: jiraColumnType === 'tag',
  };
}

export function selectJiraColumnOptions(
  field: DatabaseField | undefined,
  jiraIssues: JiraIssuesState | undefined
): JiraColumnOption[] {
  if (!field || field.type !== DatabaseFieldType.JIRA_ISSUE_DETAILS) {
    return [];
  }

  return getUniqueJiraColumnValues(
    field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
    jiraIssues
  );
}

export interface JiraIssueContext {
  issueKey: string;
  hostname?: string;
  cloudId?: string;
  ari?: string;
  projectId?: string;
}

export function selectFirstResolvedJiraIssue(
  jiraIssues: JiraIssuesState | undefined
): ResolvedJiraIssueRef | undefined {
  if (!jiraIssues) {
    return undefined;
  }

  for (const issues of Object.values(jiraIssues)) {
    for (const issue of Object.values(issues)) {
      if (issue.key) {
        return issue;
      }
    }
  }

  return undefined;
}

export function selectJiraIssueContext(
  jiraIssues: JiraIssuesState | undefined
): JiraIssueContext | undefined {
  const issue = selectFirstResolvedJiraIssue(jiraIssues);
  if (!issue) {
    return undefined;
  }

  let hostname: string | undefined;
  if (issue.url) {
    try {
      hostname = new URL(issue.url).hostname;
    } catch {
      hostname = undefined;
    }
  }

  return {
    issueKey: issue.key,
    hostname,
    cloudId: issue.cloudId,
    ari: issue.ari,
    projectId: issue.projectId,
  };
}

const parseRichText = (value: RichText): string => {
  try {
    if (value.type === 'adf') {
      const smartLinkNodeToUrl: (node: PMNode) => string = (node) => node.attrs.url ?? '';

      const schemaSmartLinkOverride = new Schema({
        nodes: defaultSchema.spec.nodes
          .update('inlineCard', {
            ...defaultSchema.spec.nodes.get('inlineCard'),
            leafText: smartLinkNodeToUrl,
          })
          .update('blockCard', {
            ...defaultSchema.spec.nodes.get('blockCard'),
            leafText: smartLinkNodeToUrl,
          })
          .update('embedCard', {
            ...defaultSchema.spec.nodes.get('embedCard'),
            leafText: smartLinkNodeToUrl,
          }),
        marks: defaultSchema.spec.marks,
      });

      const adf = JSON.parse(value.text);
      return PMNode.fromJSON(schemaSmartLinkOverride, {
        ...adf,
        content: [...adf.content.slice(0, 2)],
      }).textContent;
    }

    return '';
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (e) {
    if (e instanceof Error) {
      logError('Error parsing richtext for export', e);
    }

    return '';
  }
};

/**
 * Returns the Jira issue field in the same database that the Jira issue details field references.
 */
export function getReferencedJiraIssueDetailsField({
  database,
  field,
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.JIRA_ISSUE>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (referencedField?.type !== DatabaseFieldType.JIRA_ISSUE) {
    return undefined;
  } else {
    return referencedField as DatabaseField<DatabaseFieldType.JIRA_ISSUE>;
  }
}

export function getReferencedJiraIssueRefs<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State
): JiraIssueRef[] {
  const referencedField = getReferencedJiraIssueDetailsField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);

  return (
    referencedValue?.value.flatMap((v) =>
      isNonEmptyJiraIssueValue(v)
        ? [
            {
              cloudId: referencedField!.settings.cloudId,
              serverId: referencedField!.settings.jiraServerId,
              key: v.key,
            },
          ]
        : []
    ) ?? []
  );
}

/**
 * Returns the Jira issues that the referenced Jira issue field references.
 */
export function getReferencedJiraIssues<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State
): ResolvedJiraIssueRef[] {
  return getReferencedJiraIssueRefs({ database, field, entry }, state).flatMap((ref) => {
    const resolved = selectJiraIssueOptional(state, ref);
    return resolved ? [resolved] : [];
  });
}

export function getReferencedJiraValues<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> &
    Optional<EntryWithContext<ObjectMode>, 'entry'>,
  state: State | undefined
): ResolvedJiraIssueRef['columns'][string][] {
  if (!entry || !state) {
    return [];
  }
  const issues = getReferencedJiraIssues({ database, field, entry }, state);
  const jiraField = field.settings.jiraField && field.settings.jiraField;

  return issues.map((issue) =>
    jiraField && issue.columns[jiraField]
      ? issue.columns[jiraField]
      : { html: '', text: '', value: '', type: 'string' }
  );
}

export function getJiraColumnTextValues(columnData: JiraIssueColumnData): string[] {
  if (columnData.type === 'tag' && isTagDataArray(columnData.data)) {
    return columnData.data.map((tag) => tag.text);
  }
  if (columnData.type === 'richtext') {
    const data = columnData.data as RichText | undefined;
    if (data) {
      const parsed = parseRichText(data);
      return parsed ? [parsed] : [];
    }
    return [];
  }
  return columnData.text ? [columnData.text] : [];
}

function matchesJiraFilterValue(
  entryValue: JiraIssueColumnData,
  filterValue: string | string[]
): boolean {
  const entryTextValues = getJiraColumnTextValues(entryValue);
  const filterTextValues = Array.isArray(filterValue) ? filterValue : [filterValue];

  if (entryTextValues.length !== filterTextValues.length) {
    return false;
  }

  const sortedEntryValues = [...entryTextValues].sort();
  const sortedFilterValues = [...filterTextValues].sort();
  return sortedEntryValues.every((value, index) => value === sortedFilterValues[index]);
}

export function isJiraIssueDetailsResolved<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State
): boolean {
  const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);
  if (issueRefs.length === 0) {
    return true;
  }
  const resolvedIssues = getReferencedJiraIssues({ database, field, entry }, state);
  return resolvedIssues.length > 0;
}

const jiraIssueDetailsFieldType: FieldType<DatabaseFieldType.JIRA_ISSUE_DETAILS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  renderExportView: ({ database, field, entry }, state) => {
    return getReferencedJiraValues({ database, field, entry }, state)
      .map((value) => {
        if (value.type === 'richtext') {
          const data = value.data as RichText | undefined;

          if (data) {
            return encodeHtml(parseRichText(data));
          }
        }

        // Convert the HTML to XHTML (for example the Priority field contains
        // unclosed img tags)
        return encodeHtml(parseStorageFormat(value.html).html());
      })
      .join('<br/>');
  },

  getInitialValue: () => undefined,

  isEmptyValue: ({ database, field, entry }, state) => {
    if (!entry || !state) {
      return true;
    }

    /*
     * If no issue refs exist (parent Jira field is empty), returns empty
     */
    const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);
    if (issueRefs.length === 0) {
      return true;
    }

    /*
     * If issue refs exist but haven't resolved yet, returns not empty to keep the field visible while loading
     */
    const resolvedIssues = getReferencedJiraIssues({ database, field, entry }, state);
    if (resolvedIssues.length === 0) {
      return false;
    }

    /*
     * If issues are resolved but the specific column (e.g., "updated") hasn't been fetched yet,
     * return false to keep the field visible while the column loads. This prevents fields like
     * "Updated" from incorrectly being hidden during the incremental fetching process.
     */
    const jiraField = field.settings.jiraField;
    const allColumnsLoaded = resolvedIssues.every(
      (issue) => jiraField && issue.columns[jiraField] !== undefined
    );
    if (!allColumnsLoaded) {
      return false;
    }

    const values = getReferencedJiraValues({ database, field, entry }, state);
    return values.every((value) => value.text.trim() === '');
  },

  isEqualValue: (context1, context2, state) => {
    const isStringFilterValue = (val: unknown): val is string =>
      typeof val === 'string' && val !== '';
    const isArrayFilterValue = (val: unknown): val is string[] =>
      Array.isArray(val) && val.length > 0 && val.every((v) => typeof v === 'string');

    const getFilterValue = (val: unknown): string | string[] | undefined => {
      if (isStringFilterValue(val) || isArrayFilterValue(val)) {
        return val;
      }
      return undefined;
    };

    const filterValue1 = !context1.entry ? getFilterValue(context1.value) : undefined;
    const filterValue2 = !context2.entry ? getFilterValue(context2.value) : undefined;

    if (filterValue1 && context2.entry) {
      const entryValues = getReferencedJiraValues(context2, state);
      return entryValues.some((v) => matchesJiraFilterValue(v, filterValue1));
    }

    if (filterValue2 && context1.entry) {
      const entryValues = getReferencedJiraValues(context1, state);
      return entryValues.some((v) => matchesJiraFilterValue(v, filterValue2));
    }

    const values1 = getReferencedJiraValues(context1, state);
    const values2 = getReferencedJiraValues(context2, state);
    return isEqual(values1, values2);
  },

  getStringRepresentation: ({ database, field, entry }, state) => {
    if (state) {
      return getReferencedJiraValues({ database, field, entry }, state)
        .map((value) => {
          if (value.type === 'richtext') {
            const data = value.data as RichText | undefined;

            if (data) {
              return parseRichText(data);
            }
          }

          return value.text;
        })
        .join('\n');
    } else {
      return '';
    }
  },

  getNumberRepresentation: (
    { database, field, entry, value },
    state,
    _recursionCount
  ) => {
    if (!entry && typeof value === 'string' && value !== '') {
      const jiraIssues = (state as { jira?: { issues?: JiraIssuesState } } | undefined)
        ?.jira?.issues;
      const jiraColumnType = getJiraColumnTypeFromState(field, jiraIssues);
      const equivalentType = getJiraFieldEquivalentType(jiraColumnType);

      if (equivalentType === DatabaseFieldType.DATE) {
        const date = parseISO(value);
        return isNaN(date.getTime()) ? undefined : date.getTime();
      }

      if (equivalentType === DatabaseFieldType.NUMBER) {
        const num = parseFloat(value);
        return isNaN(num) ? undefined : num;
      }

      return undefined;
    }

    if (!state) {
      return undefined;
    }

    const values = getReferencedJiraValues({ database, field, entry }, state);
    if (values.length === 0) {
      return undefined;
    }

    const firstValue = values[0];
    const equivalentType = getJiraFieldEquivalentType(firstValue.type);

    if (equivalentType === DatabaseFieldType.DATE) {
      const date = parseISO(firstValue.text);
      return isNaN(date.getTime()) ? undefined : date.getTime();
    }

    if (equivalentType === DatabaseFieldType.NUMBER) {
      const num = parseFloat(firstValue.text);
      return isNaN(num) ? undefined : num;
    }

    return undefined;
  },

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase }
  ) => {
    const jiraField = field.settings.jiraField;
    if (jiraField) {
      const issueRefs = selector((state) => {
        const _db = selectDatabase(state, { contentId });
        const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
        const entry =
          database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
        return database && entry
          ? getReferencedJiraIssueRefs({ database, field, entry }, state)
          : [];
      });

      for (const ref of issueRefs) {
        dispatch(loadJiraIssue(ref, { columns: [jiraField] }));
      }
    }
  },

  noDefaultValue: true,
};

export default jiraIssueDetailsFieldType;
